/**
 * How the transcript fold turns a `show_page` call into its page. A live call's
 * page comes from its `tool.called` event, unless the event cut the page short
 * or hid part of it as a possible secret; then the page waits for the saved
 * message, which carries it whole. Like a suggestion, the page is not a trace step.
 */
import type { AgentPage } from '@/lib/code-sessions/agent-page';
import type { SessionMessageAssistant } from '@opencode/client/promise';

import type { SessionPart } from './transcript-model';

import { isAlteredEventString } from '@/lib/code-sessions/live-event-strings';
import { showPageInputSchema } from '@/lib/code-sessions/session-tools/show-page';

type ToolContent = Extract<SessionMessageAssistant['content'][number], { type: 'tool' }>;

export type AgentPagePart = Extract<SessionPart, { type: 'page' }>;

function parsePage(input: unknown): AgentPage | null {
  const parsed = showPageInputSchema.safeParse(input);
  // The JSON Schema lets a blank title through, and the model was told the page shows.
  return parsed.success ? { ...parsed.data, title: parsed.data.title.trim() || 'Page' } : null;
}

/**
 * A saved `show_page` call as its page. An errored call shows nothing:
 * OpenCode hands the error back to the model, which calls again. A streaming
 * call's input is partial JSON text, so its page waits for the input.
 */
export function savedAgentPagePart(
  content: ToolContent,
  completed: boolean,
): AgentPagePart | undefined {
  if (content.state.status === 'error') {
    return undefined;
  }
  const running =
    !completed && (content.state.status === 'running' || content.state.status === 'streaming');
  const page = content.state.status === 'streaming' ? null : parsePage(content.state.input);
  if (page === null && !running) {
    return undefined;
  }
  return { type: 'page', callId: content.id, page, running: running ? true : undefined };
}

/** A live call's page from its `tool.input.started` event, before its input is in. */
export function liveAgentPagePart(callId: string): AgentPagePart {
  return { type: 'page', callId, page: null, running: true };
}

/**
 * A live call's later events: `tool.called` brings the page unless the event
 * changed it, the call stops running when it succeeds, and a failed call leaves
 * the transcript, since the model sees the error and calls again.
 */
export function foldAgentPageEvent(
  parts: SessionPart[],
  part: AgentPagePart,
  type: string,
  data: Record<string, unknown>,
): void {
  switch (type) {
    case 'session.tool.called': {
      const page = parsePage(data.input);
      part.page =
        page === null || isAlteredEventString(page.html) || isAlteredEventString(page.title)
          ? null
          : page;
      return;
    }
    case 'session.tool.success':
      part.running = undefined;
      return;
    case 'session.tool.failed': {
      const index = parts.indexOf(part);
      if (index !== -1) {
        parts.splice(index, 1);
      }
    }
  }
}
